/**
 * Opens and scrolls to the product "How to use" block when the URL
 * contains ?scroll=how-to-use. Other query params are ignored.
 * No-ops on every other product URL.
 *
 * How to Use is inside the product lazy-load section. That section is
 * fetched only after .prod-lazy-loaded-sections enters the viewport, so
 * this file scrolls to the loader, waits until the section is injected,
 * then opens the block.
 *
 * New PDP: How to Use is a tab in [data-new-product-tabs], not an accordion.
 * Classic PDP: it is details.hdt-product-accordion__item.how-to-use.
 * Opening an hdt-accordion still goes through the summary click handler
 * in global.js. This file does not restyle either layout.
 */

const HOW_TO_USE = 'how-to-use';
const LAZY_SECTION = '.prod-lazy-loaded-sections';
const WAIT_MS = 20000;

function shouldOpen() {
  return new URLSearchParams(window.location.search).get('scroll') === HOW_TO_USE;
}

function scrollToElement(element) {
  const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
  const top = element.getBoundingClientRect().top + window.scrollY - headerHeight;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

function locate() {
  const tabs = document.querySelector('[data-new-product-tabs]');
  const button = tabs?.querySelector(`button[data-tab="${HOW_TO_USE}"]`);
  if (button) return { type: 'tab', tabs, button };

  const details = document.querySelector(`details.hdt-product-accordion__item.${HOW_TO_USE}`);
  if (details) return { type: 'accordion', details };

  return null;
}

function reveal(found) {
  if (found.type === 'tab') {
    if (found.button.getAttribute('aria-current') !== 'true') found.button.click();
    return found.tabs;
  }

  if (!found.details.open) found.details.querySelector('summary')?.click();
  return found.details;
}

function scrollToLoader() {
  const loader = document.querySelector(LAZY_SECTION);
  if (loader && !locate()) scrollToElement(loader);
}

function whenSectionLoads() {
  const found = locate();
  if (found) return Promise.resolve(found);

  scrollToLoader();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scrollToLoader, { once: true });
  }

  const root = document.querySelector(LAZY_SECTION) || document.body;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(value);
    };

    const observer = new MutationObserver(() => {
      const next = locate();
      if (next) finish(next);
    });

    observer.observe(root, { childList: true, subtree: true });
    const timer = window.setTimeout(() => finish(locate()), WAIT_MS);
  });
}

if (shouldOpen()) {
  whenSectionLoads().then((found) => {
    if (!found) return;
    const target = reveal(found);
    scrollToElement(target);
    window.setTimeout(() => scrollToElement(target), 400);
  });
}
